import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // checkAuth is stable - axios and API_URL are external constants
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkAuth = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('admin_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password }
    );
    
    // Store token in localStorage instead of cookies
    if (response.data.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
    }
    
    setUser(response.data.user || response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        await axios.post(
          `${API_URL}/api/auth/logout`, 
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
    } catch (error) {
      // Ignore errors on logout
    } finally {
      localStorage.removeItem('admin_token');
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
